import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "./AdminLayout";
import { adminPost, getAdminSession, readImageSize } from "./adminApi";
import "./admin.css";

const S3_PATH = process.env.REACT_APP_S3_PATH || "";
const CANDIES = [1, 2, 3, 4, 5];

const iconUrl = (name, bust) =>
  /^https?:\/\//i.test(name) ? name : `${S3_PATH}${name}${bust ? `?v=${bust}` : ""}`;

// Port of themeUpdate.php: replace each of the 5 candy icons (PNG, 70x70).
export default function AdminCandyIcons() {
  const { themeName } = useParams(); // theme id
  const navigate = useNavigate();
  const session = getAdminSession();
  const [icons, setIcons] = useState(null); // { 1: "candy1.png", ... }
  const [busy, setBusy] = useState(null); // candy index being uploaded
  const [bust, setBust] = useState({});
  const [picked, setPicked] = useState({}); // candy index -> object URL of the chosen (not yet uploaded) file
  const [broken, setBroken] = useState({}); // candy index -> current icon failed to load

  useEffect(() => {
    if (!session) {
      navigate("/admin", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await adminPost("fetchThemeData", { currentTheme: themeName }, session);
        const d = res.data || {};
        setIcons(Object.fromEntries(CANDIES.map((n) => [n, d[`candy_icon${n}`] || `candy${n}.png`])));
      } catch (err) {
        Swal.fire({ icon: "error", title: err.message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeName]);

  if (!session) return null;

  const validate = async (file) => {
    if (file.name.split(".").pop().toLowerCase() !== "png") return "Only PNG files are allowed.";
    try {
      const [w, h] = await readImageSize(file);
      if (w !== 70 || h !== 70) return "Image size must be 70x70";
    } catch (err) {
      return "Failed to load image";
    }
    return null;
  };

  const setPickedUrl = (n, url) =>
    setPicked((prev) => {
      if (prev[n]) URL.revokeObjectURL(prev[n]);
      return { ...prev, [n]: url };
    });

  // choosing a file uploads it right away; the preview shows it while the upload runs
  const onPick = async (n, e) => {
    const input = e.target;
    const file = input.files[0];
    if (!file) return setPickedUrl(n, null);
    const error = await validate(file);
    if (error) {
      input.value = "";
      setPickedUrl(n, null);
      return Swal.fire({ icon: "error", title: error });
    }
    setPickedUrl(n, URL.createObjectURL(file));

    const form = new FormData();
    form.append("file", file);
    form.append("candy_index", n);
    form.append("currentTheme", themeName);

    setBusy(n);
    try {
      const res = await adminPost("uploadCandyIcon", form, session);
      setIcons((prev) => ({ ...prev, [n]: res.url || res.filename }));
      setBust((prev) => ({ ...prev, [n]: Date.now() }));
      setBroken((prev) => ({ ...prev, [n]: false }));
      setPickedUrl(n, null);
      input.value = "";
      Swal.fire({ icon: "success", title: "Successfully Updated", timer: 1200, showConfirmButton: false });
    } catch (err) {
      setPickedUrl(n, null);
      input.value = "";
      Swal.fire({ icon: "error", title: err.message || "Upload failed" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminLayout session={session} title="Candy Icons" backTo={`/admin/rules/${encodeURIComponent(themeName)}`}>
      <div className="adm-card">
        <h2 className="adm-section-title">Add Candy Icons</h2>
        <p className="adm-note adm-note-red">
          * PNG only<br />* Size 70x70<br />* Uploads as soon as you choose a file
        </p>
        {icons === null ? (
          <p>Loading...</p>
        ) : (
          <div className="adm-candy-row">
            {CANDIES.map((n) => (
              <div className="adm-candy" key={n}>
                <h4>Add Candy {n}</h4>
                <input
                  type="file"
                  accept=".png,image/png"
                  disabled={busy !== null}
                  onChange={(e) => onPick(n, e)}
                />
                <h5>{busy === n ? `Uploading Candy ${n}...` : `Preview Candy ${n}`}</h5>
                {picked[n] || !broken[n] ? (
                  <img
                    className="adm-candy-preview"
                    src={picked[n] || iconUrl(icons[n], bust[n])}
                    alt={`Candy ${n}`}
                    onError={() => !picked[n] && setBroken((prev) => ({ ...prev, [n]: true }))}
                  />
                ) : (
                  <p className="adm-note">No icon uploaded yet</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
